// Axios instance for the touliu admin server.
//
// - Reads token from the zustand auth store and stamps it on every outgoing
//   request as both `X-Touliu-Admin-Token` (what the AdminAuth middleware
//   expects) and `Authorization: Bearer` (for curl/Postman parity).
// - Normalizes the {code, data, request_id, page, cursor, message} envelope
//   into a Result<T> so callers don't repeat the unwrap dance.
// - On 401, clears the token and broadcasts an event so <PrivateRoute> can
//   bounce the user back to /login.

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { env } from '@/utils/env';
import { useAuthStore } from '@/stores/auth';

export interface PageInfo {
  page: number;
  page_size: number;
  total: number;
}

export interface CursorInfo {
  next?: string;
  limit: number;
}

export interface Envelope<T> {
  code: string;
  data: T;
  request_id: string;
  page?: PageInfo;
  cursor?: CursorInfo;
  message?: string;
  details?: Record<string, unknown>;
}

export interface ApiError extends Error {
  code: string;
  status?: number;
  requestId?: string;
  details?: Record<string, unknown>;
}

function makeApiError(
  status: number | undefined,
  payload: Partial<Envelope<unknown>> | undefined,
  fallbackMsg: string,
): ApiError {
  const e = new Error(payload?.message || fallbackMsg) as ApiError;
  e.code = payload?.code || 'INTERNAL';
  e.status = status;
  e.requestId = payload?.request_id;
  e.details = payload?.details;
  return e;
}

/** Custom event raised when the server says we're not authed. */
export const AUTH_ERROR_EVENT = 'touliu:admin:unauthorized';

const instance: AxiosInstance = axios.create({
  baseURL: env.apiBase || '',
  timeout: 30_000,
  // Sane defaults — admin endpoints all speak JSON.
  headers: { 'Content-Type': 'application/json' },
});

instance.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers = config.headers ?? {};
    config.headers['X-Touliu-Admin-Token'] = token;
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

instance.interceptors.response.use(
  (res) => res,
  (err: AxiosError<Envelope<unknown>>) => {
    const status = err.response?.status;
    if (status === 401) {
      useAuthStore.getState().clear();
      window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT));
    }
    return Promise.reject(
      makeApiError(status, err.response?.data, err.message || 'network error'),
    );
  },
);

// ---------------------------------------------------------------------------
// Typed helpers
// ---------------------------------------------------------------------------

export interface ListResult<T> {
  data: T[];
  page?: PageInfo;
  cursor?: CursorInfo;
}

async function request<T>(config: AxiosRequestConfig): Promise<Envelope<T>> {
  const res: AxiosResponse<Envelope<T>> = await instance.request(config);
  // 204 No Content arrives with empty body — synthesize an envelope.
  if (!res.data) {
    return { code: 'OK', data: undefined as unknown as T, request_id: '' };
  }
  return res.data;
}

/** Loose query-param object accepted by GET helpers. */
export type QueryParams = Record<string, unknown> | object | undefined;

/** GET that expects a single payload. */
export async function get<T>(url: string, params?: QueryParams): Promise<T> {
  const env = await request<T>({ url, method: 'GET', params });
  return env.data;
}

/** GET that expects a list payload (data + page/cursor metadata). */
export async function list<T>(
  url: string,
  params?: QueryParams,
): Promise<ListResult<T>> {
  const envResp = await request<T[]>({ url, method: 'GET', params });
  return {
    data: envResp.data ?? [],
    page: envResp.page,
    cursor: envResp.cursor,
  };
}

export async function post<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const env = await request<T>({ url, method: 'POST', data: body, ...config });
  return env.data;
}

export async function put<T>(url: string, body?: unknown): Promise<T> {
  const env = await request<T>({ url, method: 'PUT', data: body });
  return env.data;
}

export async function patch<T>(url: string, body?: unknown): Promise<T> {
  const env = await request<T>({ url, method: 'PATCH', data: body });
  return env.data;
}

export async function del<T = void>(url: string): Promise<T> {
  const env = await request<T>({ url, method: 'DELETE' });
  return env.data;
}

export default instance;
